import { useAtomValue, useSetAtom } from 'jotai'
import { CSSProperties, ReactNode, useState } from 'react'
import { WidgetInputComplexNumericField, WidgetLabel } from '../../../../rapid-ui'
import { highlightedLayerIdAtom, selectedLayerIdAtom } from '../../atoms'
import { useCanvasStore } from '../../canvas-store'
import type { Spacing } from '../../data/v1'

function LockToggle({ pressed, onClick, label }: { pressed: boolean; onClick: () => void; label: string }) {
	return (
		<button
			type="button"
			onClick={onClick}
			aria-pressed={pressed}
			className={`cursor-pointer rounded px-1.5 py-0.5 text-[10px] flex items-center gap-1 uppercase font-medium transition-colors ${
				pressed
					? 'bg-blue-100 text-blue-600'
					: 'text-grey-400 hover:bg-grey-100 hover:text-grey-500'
			}`}
		>
			<svg xmlns="http://www.w3.org/2000/svg" width={10} height={10} viewBox="0 0 10 10">
				<path
					d="M4.79 0.00587C4.77075 0.00809 4.71225 0.0148 4.66 0.02078C4.10951 0.08379 3.5737400000000004 0.33169 3.14 0.7241C2.72979 1.09521 2.42297 1.6315899999999999 2.30917 2.1765600000000003C2.25682 2.42724 2.25687 2.42626 2.2527000000000004 3.2469200000000003L2.24878 4.01884 2.19189 4.03054C2.09737 4.04998 1.9398300000000002 4.10506 1.84264 4.15264C1.47607 4.3321 1.20137 4.6485199999999995 1.07366 5.0384C1.00057 5.2615300000000005 1.0050000000000001 5.13485 1.0050000000000001 7C1.0050000000000001 8.86515 1.00057 8.73847 1.07366 8.9616C1.2244 9.42179 1.57815 9.77557 2.03822 9.92628C2.26304 9.99993 2.0081100000000003 9.99372 4.95 9.99722C7.863630000000001 10.000680000000001 7.70366 10.00379 7.92032 9.93942C8.41665 9.79198 8.79756 9.409550000000001 8.94037 8.91531C9.00246 8.70043 9.00079 8.75809 8.99718 6.95C8.993540000000001 5.12427 8.99852 5.25875 8.92628 5.03822C8.798630000000001 4.64853 8.523859999999999 4.33207 8.15736 4.15264C8.060170000000001 4.10506 7.90263 4.04998 7.80811 4.03054L7.75122 4.01884 7.7473 3.2469200000000003C7.74313 2.42626 7.74318 2.42724 7.69083 2.1765600000000003C7.52415 1.3783699999999999 6.9859100000000005 0.6762300000000001 6.255 0.30349C5.96718 0.15671 5.70349 0.07285 5.37641 0.02408C5.28015 0.00973 4.86753 -0.00309 4.79 0.00587M4.83694 1.0106C4.523000000000001 1.0384600000000002 4.19873 1.16496 3.94 1.3605C3.84382 1.4332 3.6832 1.59382 3.6105 1.69C3.4513700000000003 1.9005500000000002 3.3406900000000004 2.14695 3.28574 2.4129300000000002C3.2563 2.55547 3.2501700000000002 2.7179 3.25009 3.3575L3.25 4 5 4L6.75 4 6.74991 3.3575C6.74987 3.00413 6.7453 2.66388 6.73977 2.6014C6.689489999999999 2.03328 6.37742 1.5374 5.88624 1.24512C5.57587 1.06044 5.206259999999999 0.9778300000000001 4.83694 1.0106M2.4050000000000002 5.01016C2.23394 5.04474 2.08462 5.17449 2.02832 5.337490000000001L2.005 5.405 2.005 7L2.005 8.595 2.02861 8.663400000000001C2.07515 8.79818 2.20182 8.924850000000001 2.3366000000000002 8.97139L2.4050000000000002 8.995000000000001 5 8.995000000000001L7.595 8.995000000000001 7.6634 8.97139C7.79818 8.924850000000001 7.92485 8.79818 7.97139 8.663400000000001L7.995 8.595 7.995 7L7.995 5.405 7.97139 5.3366C7.92598 5.205080000000001 7.80146 5.07848 7.66866 5.02881L7.605 5.005 5.025 5.00354C3.6060000000000003 5.002730000000001 2.427 5.00572 2.4050000000000002 5.01016M4.915 6.12608C4.68765 6.15944 4.53415 6.234030000000001 4.38409 6.3840900000000005C4.2081 6.56008 4.128 6.752730000000001 4.128 7C4.128 7.24727 4.2081 7.43992 4.38409 7.61591C4.55797 7.789790000000001 4.7592300000000005 7.87419 5 7.87419C5.24077 7.87419 5.44203 7.789790000000001 5.61591 7.61591C5.79225 7.43957 5.872269999999999 7.245570000000001 5.87201 6.995C5.871760000000001 6.752630000000001 5.79042 6.55861 5.61591 6.3840900000000005C5.48785 6.25603 5.35977 6.18429 5.1904200000000005 6.14578C5.11744 6.12918 4.96715 6.11843 4.915 6.12608"
					stroke="none"
					fill="currentColor"
					fillRule="evenodd"
					strokeWidth={0.01}
				/>
			</svg>
			{label}
		</button>
	)
}

type CrossLayoutProps = {
	top: ReactNode
	left: ReactNode
	right: ReactNode
	bottom: ReactNode
}

function CrossLayout({ top, left, right, bottom }: CrossLayoutProps) {
	return (
		<>
			<div className="absolute inset-0 flex items-center justify-center">
				{/* Extra height here because of the gap-4 in the flex-col below */}
				<div className="ring-grey-200 ring-1 w-[calc(50%+(var(--widget-spacing)/2))] h-[calc(50%+32px)]">
				</div>
			</div>
			<div className="flex flex-col gap-4">
				<div className="flex items-center justify-center px-[var(--widget-spacing)]">
					{top}
				</div>
				<div className="flex items-center justify-between gap-[var(--widget-spacing)]">
					{left}
					{right}
				</div>
				<div className="flex items-center justify-center px-[var(--widget-spacing)]">
					{bottom}
				</div>
			</div>
		</>
	)
}

type LockTogglesProps = {
	lock: LockMode
	setLock: (mode: LockMode) => void
}

function LockToggles({ lock, setLock }: LockTogglesProps) {
	return (
		<div className="flex items-center gap-0.5">
			<LockToggle
				label="All"
				pressed={lock === 'all'}
				onClick={() => setLock(lock === 'all' ? 'none' : 'all')}
			/>
			<LockToggle
				label="T/B"
				pressed={lock === 'tb'}
				onClick={() => setLock(lock === 'tb' ? 'none' : 'tb')}
			/>
			<LockToggle
				label="L/R"
				pressed={lock === 'lr'}
				onClick={() => setLock(lock === 'lr' ? 'none' : 'lr')}
			/>
		</div>
	)
}

export type SpacingInspectorProps = {
	value: Spacing
	onChange: (value: Spacing) => void
	onTransientChange?: (value: Spacing) => void
}

type LockMode = 'none' | 'all' | 'tb' | 'lr'

export function SpacingInspector({ value, onChange, onTransientChange }: SpacingInspectorProps) {
	const store = useCanvasStore()
	const selectedLayerId = useAtomValue(selectedLayerIdAtom, { store })
	const setHighlightedLayerId = useSetAtom(highlightedLayerIdAtom, { store })
	const [paddingLock, setPaddingLock] = useState<LockMode>('none')
	const [marginLock, setMarginLock] = useState<LockMode>('none')

	function update<K extends keyof Spacing>(key: K, v: Spacing[K], lock: LockMode, transient = false) {
		const cb = transient && onTransientChange ? onTransientChange : onChange
		const isPadding = key.startsWith('padding')
		const isTB = key.endsWith('Top') || key.endsWith('Bottom')

		if (lock === 'all') {
			if (isPadding) {
				cb({ ...value, paddingTop: v, paddingBottom: v, paddingLeft: v, paddingRight: v })
			} else {
				cb({ ...value, marginTop: v, marginBottom: v, marginLeft: v, marginRight: v })
			}
		} else if (lock === 'tb' && isTB) {
			if (isPadding) {
				cb({ ...value, paddingTop: v, paddingBottom: v })
			} else {
				cb({ ...value, marginTop: v, marginBottom: v })
			}
		} else if (lock === 'lr' && !isTB) {
			if (isPadding) {
				cb({ ...value, paddingLeft: v, paddingRight: v })
			} else {
				cb({ ...value, marginLeft: v, marginRight: v })
			}
		} else {
			cb({ ...value, [key]: v })
		}
	}

	return (
		<div
			className="grid gap-4"
			style={{ ['--widget-spacing']: 'calc(var(--toolbar-width) * 0.22)' } as CSSProperties}
			onFocusCapture={() => setHighlightedLayerId(selectedLayerId)}
			onBlurCapture={() => setHighlightedLayerId(null)}
		>
			<WidgetLabel
				label="Padding"
				trailing={<LockToggles lock={paddingLock} setLock={setPaddingLock} />}
			>
				<CrossLayout
					top={
						<WidgetInputComplexNumericField
							label="Top"
							innerLabel="T"
							value={value.paddingTop}
							onChange={(v) => update('paddingTop', v, paddingLock)}
							onTransientChange={onTransientChange
								? (v) => update('paddingTop', v, paddingLock, true)
								: undefined}
							min={0}
							suffix="px"
						/>
					}
					left={
						<WidgetInputComplexNumericField
							label="Left"
							innerLabel="L"
							value={value.paddingLeft}
							onChange={(v) => update('paddingLeft', v, paddingLock)}
							onTransientChange={onTransientChange
								? (v) => update('paddingLeft', v, paddingLock, true)
								: undefined}
							min={0}
							suffix="px"
						/>
					}
					right={
						<WidgetInputComplexNumericField
							label="Right"
							innerLabel="R"
							value={value.paddingRight}
							onChange={(v) => update('paddingRight', v, paddingLock)}
							onTransientChange={onTransientChange
								? (v) => update('paddingRight', v, paddingLock, true)
								: undefined}
							min={0}
							suffix="px"
						/>
					}
					bottom={
						<WidgetInputComplexNumericField
							label="Bottom"
							innerLabel="B"
							value={value.paddingBottom}
							onChange={(v) => update('paddingBottom', v, paddingLock)}
							onTransientChange={onTransientChange
								? (v) => update('paddingBottom', v, paddingLock, true)
								: undefined}
							min={0}
							suffix="px"
						/>
					}
				/>
			</WidgetLabel>
			<WidgetLabel
				label="Margin"
				trailing={<LockToggles lock={marginLock} setLock={setMarginLock} />}
			>
				<CrossLayout
					top={
						<WidgetInputComplexNumericField
							label="Top"
							innerLabel="T"
							value={value.marginTop}
							onChange={(v) => update('marginTop', v, marginLock)}
							onTransientChange={onTransientChange
								? (v) => update('marginTop', v, marginLock, true)
								: undefined}
							suffix="px"
						/>
					}
					left={
						<WidgetInputComplexNumericField
							label="Left"
							innerLabel="L"
							value={value.marginLeft}
							onChange={(v) => update('marginLeft', v, marginLock)}
							onTransientChange={onTransientChange
								? (v) => update('marginLeft', v, marginLock, true)
								: undefined}
							suffix="px"
						/>
					}
					right={
						<WidgetInputComplexNumericField
							label="Right"
							innerLabel="R"
							value={value.marginRight}
							onChange={(v) => update('marginRight', v, marginLock)}
							onTransientChange={onTransientChange
								? (v) => update('marginRight', v, marginLock, true)
								: undefined}
							suffix="px"
						/>
					}
					bottom={
						<WidgetInputComplexNumericField
							label="Bottom"
							innerLabel="B"
							value={value.marginBottom}
							onChange={(v) => update('marginBottom', v, marginLock)}
							onTransientChange={onTransientChange
								? (v) => update('marginBottom', v, marginLock, true)
								: undefined}
							suffix="px"
						/>
					}
				/>
			</WidgetLabel>
		</div>
	)
}
